<script lang="ts" setup>
import { computed, nextTick, onMounted, reactive, Ref, ref, watch } from "vue";
import { onKeyStroke } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import {
  useToast,
  PDropdown,
  PDialog,
  PButton,
  IconSettingsMd as SettingsIcon,
  IconCheckboxDefault as CheckIcon,
  IconCheckboxActive as CheckActiveIcon,
  IconFilledEditMd as EditIcon,
  IconCrossMd as CrossIcon,
  IconTrashMd as TrashIcon,
  IconPlusSm as PlusIcon,
  IconFilledMoveSm as FilledMoveIcon,
} from "@profeat/ui-kit";

import { useContacts } from "~/stores/contacts.store";
import {
  CustomFieldType,
  StaticFieldType,
  SystemFieldType,
} from "~/entities/contacts";
import { useUploadStore } from "~/stores/upload.store";
import { useSortable } from "@vueuse/integrations/useSortable";
import ModalAddField from "~/components/contacts/ModalAddField.vue";

interface Field {
  enable: boolean;
  label: string;
  id: string;
  model: string;
  editable: boolean;
  duplicateError: boolean;
  isFocus: boolean;
  group: "system" | "custom" | "static";
}

const props = defineProps({
  editable: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const defaultTimeout = 5000;

const emit = defineEmits<{
  (
    event: "remove-field",
    params: { fieldId: string; fieldGroup: "system" | "custom" | "static" }
  ): void;
  (
    event: "add-field",
    params: { fieldId: string; fieldGroup: "system" | "custom" | "static" }
  ): void;
}>();

const settingsVisible = ref(false);
const removeDialog = ref(false);
const removedField = ref({ name: "", id: "" });
const removingTime = ref<ReturnType<typeof setTimeout>>();
const inputRefs = ref<HTMLInputElement[]>([]);
const modalAddField = ref<InstanceType<typeof ModalAddField>>();
const listId = ref(Math.random());
const checkboxFields = ref<Field[]>([]);

const { t } = useI18n();
const contactsStore = useContacts();
const { showToast } = useToast();
const { fetchColumns } = useUploadStore();

const getCheckboxFields = (): Field[] => {
  const fields = contactsStore.getEntireFields;
  return fields.map((f) => {
    let field: Field;
    switch (f.group) {
      case "custom": {
        field = {
          id: f.id,
          label: f.name,
          model: f.name,
          enable: f.options.visible !== false,
          duplicateError: false,
          isFocus: false,
          editable: true,
          group: f.group,
        };
        break;
      }
      case "system": {
        field = {
          id: f.id,
          label: t(`customers.header.${f.name}`),
          model: t(`customers.header.${f.name}`),
          enable: f.options.visible !== false,
          duplicateError: false,
          isFocus: false,
          editable: false,
          group: f.group,
        };
        break;
      }
      default: {
        field = {
          id: f.id,
          label: t(`customers.header.${f.name}`),
          model: t(`customers.header.${f.name}`),
          enable: f.options.visible !== false,
          duplicateError: false,
          isFocus: false,
          editable: false,
          group: f.group,
        };
        break;
      }
    }
    return field;
  });
};

const onChange = (_: Event, index: number) => {
  const fieldId = checkboxFields.value[index].id;
  const fieldGroup = checkboxFields.value[index].group;
  const enabled = checkboxFields.value[index].enable;

  if (!contactsStore.fieldOptionsUpdating) {
    checkboxFields.value[index].enable = !enabled;
  }

  if (checkboxFields.value[index].enable) {
    emit("add-field", { fieldId, fieldGroup });
  } else {
    emit("remove-field", { fieldId, fieldGroup });
  }
};
const getCheckIcon = (payload: boolean) => {
  return payload ? CheckActiveIcon : CheckIcon;
};
const onFieldEdit = (event: Event, index: number) => {
  let value = (event.target as HTMLInputElement).value;

  if (value.trim().length === 0) value = "";
  checkboxFields.value[index].duplicateError = checkboxFields.value.some(
    (item) =>
      item.id !== checkboxFields.value[index].id &&
      item.model.toLocaleLowerCase().trim() === value.toLocaleLowerCase().trim()
  );
  checkboxFields.value[index].model = value;
};
const editField = (index: number) => {
  const inputElement = inputRefs.value[index];
  if (inputElement) inputElement.focus();
};
const undoChange = (index: number) => {
  const inputElement = inputRefs.value[index];

  checkboxFields.value[index].model = checkboxFields.value[index].label;
  inputElement.blur();
};
const toggleFocus = async (payload: boolean, index: number) => {
  await nextTick();

  if (!checkboxFields.value[index].editable) return;

  checkboxFields.value[index].isFocus = payload;
  const field = checkboxFields.value[index];

  if (field.duplicateError) {
    // находит ближайший индекс для уникального имени
    let i = 1;
    while (
      checkboxFields.value.some(
        (x) =>
          x.id !== field.id &&
          x.model.toLocaleLowerCase().trim() ===
            generateName(field.model, String(i)).toLocaleLowerCase().trim()
      )
    ) {
      i++;
    }

    field.model = generateName(field.model, String(i));
    await contactsStore.updateField({ fieldId: field.id, name: field.model });
  }

  await contactsStore.updateField({ fieldId: field.id, name: field.model });
  checkboxFields.value[index].duplicateError = false;

  function generateName(base: string, suffix: string): string {
    return `${base.trim()} ${suffix.trim()}`;
  }
};
const setRemovingFiled = (payload: { name: string; id: string }) => {
  removedField.value.name = payload.name;
  removedField.value.id = payload.id;
  removeDialog.value = true;
  settingsVisible.value = false;
};
const cancelRemove = () => clearTimeout(removingTime.value);
const removeField = () => {
  showToast({
    text: `Колонка «${removedField.value.name}» удалена`,
    actions: [{ name: "Отменить удаление", action: cancelRemove }],
    timer: defaultTimeout,
  });

  removingTime.value = setTimeout(async () => {
    await contactsStore.removeField(removedField.value.id);
  }, defaultTimeout);

  removeDialog.value = false;
};

onKeyStroke("Enter", async () => {
  const index = checkboxFields.value.findIndex(({ isFocus }) => isFocus);

  if (index !== -1 && !checkboxFields.value[index].duplicateError) {
    inputRefs.value[index].blur();

    const field = checkboxFields.value[index];
    await contactsStore.updateField({ fieldId: field.id, name: field.model });
  }
});
onKeyStroke("Escape", () => {
  const index = checkboxFields.value.findIndex(({ isFocus }) => isFocus);

  if (index !== -1) undoChange(index);
});
onMounted(() => {
  checkboxFields.value = getCheckboxFields();
});

/**
 * modalAddField dataType => FieldType
 */
const CustomFieldTypeMap = {
  date: CustomFieldType.Date,
  number: CustomFieldType.Int,
  string: CustomFieldType.Text,
};

/**
 * Проверяет, является ли тип допустимым
 */
const isCustomFieldType = (
  dataType: any
): dataType is keyof typeof CustomFieldTypeMap => {
  const allowedDataTypes: string[] = Object.keys(CustomFieldTypeMap);
  return allowedDataTypes.includes(dataType);
};

/**
 * Создает новое поле, перегружает таблицу
 * **/
const handleAddField = async () => {
  settingsVisible.value = false;
  const result = await modalAddField.value?.show().catch(() => null);
  if (
    !result ||
    result.dataType === null ||
    !isCustomFieldType(result.dataType)
  )
    return;
  await contactsStore
    .addField(result.name, CustomFieldTypeMap[result.dataType])
    .then(async () => {
      showToast({
        color: "success",
        text: t("customers.upload.column added", {
          name: result.name,
        }),
        timer: defaultTimeout,
      });
      await contactsStore.reloadContacts();
    });
};

const getOrderedFields = (oldIndex: number, newIndex: number) => {
  const unit = Math.sign(newIndex - oldIndex);
  let index = oldIndex;
  let current = checkboxFields.value[index];
  if (current.group === "static") {
    return [];
  }
  const fields = [
    {
      fieldId: current.id,
      isSysField: current.group === "system",
      order: newIndex,
    },
  ];
  do {
    index += unit;
    current = checkboxFields.value[index];
    fields.push({
      fieldId: current.id,
      isSysField: current.group === "system",
      order: index - unit,
    });
  } while (index !== newIndex);
  return fields;
};

watch(
  () => contactsStore.getEntireFields,
  () => (checkboxFields.value = getCheckboxFields())
);

watch(
  () => settingsVisible.value,
  (isVisible) => {
    if (isVisible) {
      listId.value = Math.random();
    }
  }
);

watch(
  () => listId.value,
  () => {
    nextTick(() => {
      useSortable("#sortable", checkboxFields, {
        animation: 150,
        handle: ".handle",
        onUpdate: async (e: any) => {
          const fields = getOrderedFields(e.oldIndex, e.newIndex);
          if (fields.length && !contactsStore.fieldOptionsUpdating) {
            await contactsStore.updateFieldOptions(fields);
          }
          listId.value = Math.random();
        },
      });
    });
  }
);
</script>

<template>
  <PDialog v-model="removeDialog" width="x-small">
    <template #header>
      <header>
        <p>{{ $t("customers.deleteField") }}?</p>
        <CrossIcon class="icon" @click="removeDialog = false" />
      </header>
    </template>

    <div class="main">
      <p>
        {{ $t("customers.deleteFieldConfirm") }}
        <span>«{{ removedField.name }}»</span>?
      </p>
    </div>

    <template #footer>
      <footer>
        <PButton color="secondary" @click="removeDialog = false">
          {{ $t("common.leave") }}
        </PButton>
        <PButton @click="removeField">{{ $t("common.delete") }}</PButton>
      </footer>
    </template>
  </PDialog>

  <PDropdown
    v-model="settingsVisible"
    :width="328"
    :max-height="400"
    :close-on-self-click="false"
    :placement="'bottom-end'"
    :no-padding="true"
  >
    <template #activator="{ click, setReference }">
      <div class="settings-icon" :ref="setReference" @click="click">
        <SettingsIcon />
      </div>
    </template>

    <template #default>
      <div class="fields-list" id="sortable" :key="listId">
        <div
          v-for="(field, index) in checkboxFields"
          :key="index"
          class="field"
          :class="{ _error: field.duplicateError }"
        >
          <div class="handle-area">
            <div class="handle" v-if="field.group !== 'static'">
              <FilledMoveIcon class="color-[#2B3238]" />
            </div>
          </div>

          <label class="checkbox">
            <input
              class="input-checkbox"
              type="checkbox"
              :value="field.enable"
              @change="onChange($event, index)"
            />

            <component
              :is="getCheckIcon(field.enable)"
              :class="{ ['color-[#91A0AE]']: !field.enable }"
            />
          </label>

          <div class="input-field">
            <input
              type="text"
              maxlength="30"
              v-model="field.model"
              @input="onFieldEdit($event, index)"
              ref="inputRefs"
              :readonly="!field.editable"
              @focus="toggleFocus(true, index)"
              @blur="toggleFocus(false, index)"
            />

            <span v-if="field.duplicateError" class="hint">
              {{ $t("chatbots.duplicateNameTag") }}
            </span>
          </div>

          <div
            :class="[
              'icon-actions',
              { none: !field.editable || !props.editable },
            ]"
          >
            <div v-if="field.isFocus" @mousedown="undoChange(index)">
              <CrossIcon class="color-[#91A0AE]" />
            </div>
            <div v-else @click="editField(index)">
              <EditIcon class="color-[#91A0AE]" />
            </div>
            <div @click="setRemovingFiled({ name: field.model, id: field.id })">
              <TrashIcon class="color-[#91A0AE]" />
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer v-if="props.editable">
      <div class="add-field-area">
        <button @click="handleAddField">
          <PlusIcon color="#2db2e3" />
          <span>{{ $t("customers.upload.add column") }}</span>
        </button>
      </div>
    </template>
  </PDropdown>
  <ModalAddField ref="modalAddField" />
</template>

<style lang="scss" scoped>
header {
  padding: 14px 16px;
  border-bottom: 1px solid var(--common-rain);

  display: flex;
  align-items: center;
  justify-content: center;

  color: var(--common-black, #2b3238);
  font-size: 16px;
  font-weight: 600;
  line-height: 150%;

  p {
    flex: 1;
    text-align: center;
  }

  .icon {
    cursor: pointer;
  }
}

.main {
  padding: 32px 16px;

  color: var(--common-black);
  text-align: center;
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;

  span {
    font-weight: 600;
  }
}

footer {
  padding: 8px 16px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  button {
    width: 158px;
  }
}

:deep(.dropdown) {
  .dropdown-content {
    &::-webkit-scrollbar {
      cursor: inherit !important;
      width: 6px;
    }
    &::-webkit-scrollbar-thumb {
      cursor: inherit !important;
      border-radius: 25px;
      background: var(--common-rain, #d0dbe4);

      &:hover {
        cursor: pointer !important;
      }
    }
    &::-webkit-scrollbar-track {
      cursor: inherit !important;
      background: transparent;
    }
  }
}

.settings-icon {
  cursor: pointer;
  display: flex;
  padding: 5px;
}

.fields-list {
  padding: 18px;
  .field {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    max-width: 100%;
    gap: 12px;
    padding: 8px 0;
    min-height: 24px;
    box-sizing: content-box;

    .checkbox {
      cursor: pointer;
      display: flex;

      .input-checkbox {
        opacity: 0;
        height: 0;
        width: 0;
      }
    }

    .input-field {
      position: relative;
      width: 164px;

      input {
        color: var(--common-black, #2b3238);
        font-size: 14px;
        line-height: 142.8%;
        text-overflow: ellipsis;
        max-width: 100%;
      }

      .hint {
        position: absolute;
        left: 0;
        top: 20px;
        color: var(--additional-error, #e46060);
        font-size: 12px;
        font-weight: 400;
        line-height: 133.3%;
      }
    }
    &._error {
      margin-bottom: 20px;
    }

    .icon-actions {
      display: flex;
      align-items: center;
      gap: 12px;
      cursor: pointer;

      div {
        display: flex;
        width: 20px;
        height: 20px;
      }
    }
    .none {
      opacity: 0;
      pointer-events: none;
    }

    .handle-area {
      width: 16px;

      .handle {
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: move;
        cursor: grab;
        cursor: -moz-grab;
        cursor: -webkit-grab;
      }
      .handle:active {
        cursor: grabbing;
        cursor: -moz-grabbing;
        cursor: -webkit-grabbing;
      }
    }
  }
}

.field:first-child {
  padding-top: 0;
}
.field:last-child {
  padding-bottom: 0;
}

.add-field-area {
  display: flex;
  align-items: center;
  height: 52px;
  width: 100%;
  padding: 16px;

  bottom: 0;
  border-top: 1px solid rgba(0, 0, 0, 0.1);

  button {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0px;
    gap: 4px;

    height: 20px;

    flex: none;
    order: 0;
    flex-grow: 0;
  }

  span {
    font-size: 14px;
    //margin-top: 2px;
    color: var(--color-primary, #2db2e3);
  }

  :deep(svg) {
    fill: var(--color-primary, #2db2e3);
  }
}
</style>
